import {
  Deploy,
  NftTokenEntryPoint,
  TransactorHashType,
} from '@block-explorer/entities';
import { MessageData } from './types';
import {
  getNftTokenIdsFromArguments,
  getNftTokensQuantityFromArgs,
  guardedDeriveSplitDataFromArguments,
} from './utils';

const getNftTransactorsDataFromArgs = (args) => {
  const tokenOwner = guardedDeriveSplitDataFromArguments(
    args.token_owner,
    'Account'
  );
  const owner = guardedDeriveSplitDataFromArguments(args.owner, 'Account');
  const sourceKey = guardedDeriveSplitDataFromArguments(
    args.source_key,
    'Account'
  );

  const targetKey = guardedDeriveSplitDataFromArguments(
    args.target_key,
    'Account'
  );
  const recipientHash = guardedDeriveSplitDataFromArguments(
    args.recipient,
    'Account'
  );

  const accountSpenderHash = guardedDeriveSplitDataFromArguments(
    args.spender,
    'Account'
  );

  const contractSpenderHash = guardedDeriveSplitDataFromArguments(
    args.spender,
    'Hash'
  );
  const operatorHash = guardedDeriveSplitDataFromArguments(
    args.operator,
    'Hash'
  );

  return {
    tokenOwner,
    owner,
    sourceKey,
    targetKey,
    recipientHash,
    contractSpenderHash,
    operatorHash,
    accountSpenderHash,
  };
};

const prepareActionMessageData = (
  deploy: Deploy,
  getPublicKeyByAccountHash: (hash: string) => any
): MessageData | null => {
  const { entryPoint, args } = deploy;
  const entryPointName = entryPoint?.name || '';
  const actionName = {
    approve: 'Approve transfer',
    burn: 'Burn',
    mint: 'Mint',
    transfer: 'Transfer',
    update_token_meta: 'Update metadata',
    set_approval_for_all: 'Approve transfer all',
  };

  if (actionName[entryPointName] === undefined) {
    return null;
  }

  const numOfTokens =
    entryPointName !== NftTokenEntryPoint.approve &&
    entryPointName !== NftTokenEntryPoint.update_token_meta
      ? getNftTokensQuantityFromArgs(args)
      : null;

  const {
    recipientHash,
    tokenOwner,
    contractSpenderHash,
    operatorHash,
    accountSpenderHash,
  } = getNftTransactorsDataFromArgs(args);

  const accountHash =
    recipientHash?.hash || tokenOwner?.hash || accountSpenderHash?.hash;

  const actionOutcome: Partial<MessageData> = {
    ...(numOfTokens && { numOfTokens: numOfTokens }),
    ...(accountHash && {
      prefix1: 'to',
      account1: {
        accountHash: accountHash,
        publicKey: accountHash && getPublicKeyByAccountHash(accountHash),
      },
    }),
    ...((contractSpenderHash?.hash || operatorHash?.hash) && {
      prefix1: 'to',
      account1: {
        hash: contractSpenderHash?.hash || operatorHash?.hash,
      },
    }),
  };

  return {
    entryPointName: entryPointName,
    actionName: actionName[entryPointName] || entryPointName,
    ...actionOutcome,
  };
};

const prepareActionMessageDataForHome = (
  deploy: Deploy,
  getPublicKeyByAccountHash: (hash: string) => any
): MessageData | null => {
  const { entryPoint, callerPublicKey, callerHash } = deploy;
  const entryPointName = entryPoint?.name || '';

  const messageObject = prepareActionMessageData(
    deploy,
    getPublicKeyByAccountHash
  );

  if (messageObject === null) {
    return null;
  }

  const actionOutcome: Partial<MessageData> = {
    callerPrefix: entryPointName === 'transfer' ? 'from' : 'by',
    callerAccount: {
      publicKey: callerPublicKey || callerHash,
    },
  };

  return {
    ...messageObject,
    ...actionOutcome,
  };
};

const prepareActionMessageDataForDeployDetails = (
  deploy: Deploy,
  getPublicKeyByAccountHash: (hash: string) => any
): MessageData | null => {
  const { entryPoint, args } = deploy;
  const entryPointName = entryPoint?.name || '';

  const actionName = {
    [NftTokenEntryPoint.transfer]: 'Transfer',
    [NftTokenEntryPoint.approve]: 'Approve transfer rights',
    [NftTokenEntryPoint.burn]: 'Burn',
    [NftTokenEntryPoint.mint]: 'Mint',
    [NftTokenEntryPoint.update_token_meta]: 'Update metadata',
    [NftTokenEntryPoint.set_approval_for_all]: 'Approve transfer rights',
  };

  const contractPrefixesMap = {
    [NftTokenEntryPoint.approve]: 'for',
    [NftTokenEntryPoint.update_token_meta]: 'for',
    [NftTokenEntryPoint.mint]: 'of',
    [NftTokenEntryPoint.set_approval_for_all]: 'of',
  };

  const senderPrefixesMap = {
    [NftTokenEntryPoint.transfer]: 'from',
    [NftTokenEntryPoint.burn]: 'owned by',
    [NftTokenEntryPoint.approve]: 'to',
    [NftTokenEntryPoint.mint]: 'to',
  };

  if (actionName[entryPointName] === undefined) {
    return null;
  }

  const nftTokenIds = getNftTokenIdsFromArguments(args);
  const {
    recipientHash,
    tokenOwner,
    contractSpenderHash,
    operatorHash,
    sourceKey,
    owner,
    targetKey,
    accountSpenderHash,
  } = getNftTransactorsDataFromArgs(args);

  const account1Hash =
    tokenOwner?.hash ||
    sourceKey?.hash ||
    owner?.hash ||
    accountSpenderHash?.hash;
  const account2Hash = recipientHash?.hash || targetKey?.hash;

  const contractPrefix = contractPrefixesMap[entryPointName];
  const senderPrefix = senderPrefixesMap[entryPointName];

  const actionOutcome: Partial<MessageData> = {
    nftTokenIds: nftTokenIds,
    contractPrefix,
    ...(account1Hash && {
      prefix1: senderPrefix,
      account1: {
        hash: account1Hash,
        publicKey: account1Hash && getPublicKeyByAccountHash(account1Hash),
        hashType: TransactorHashType.account,
      },
    }),
    ...(contractSpenderHash?.hash && {
      prefix1: senderPrefix,
      account1: {
        hash: contractSpenderHash?.hash,
        hashType: TransactorHashType.hash,
      },
    }),
    ...(account2Hash && {
      account2: {
        hash: account2Hash,
        publicKey: account2Hash && getPublicKeyByAccountHash(account2Hash),
        hashType: TransactorHashType.account,
      },
    }),
    ...(operatorHash?.hash && {
      account2: {
        hash: operatorHash?.hash,
        hashType: TransactorHashType.hash,
      },
    }),
  };

  return {
    entryPointName: entryPointName,
    actionName: actionName[entryPointName] || entryPointName,
    ...actionOutcome,
  };
};

export const nftTokenMessage = () => {
  const getRelevantAccountHashesFromArgs = () => {
    return [
      'token_owner',
      'recipient',
      'spender',
      'operator',
      'target_key',
      'source_key',
    ];
  };

  return {
    prepareActionMessageData,
    prepareActionMessageDataForHome,
    prepareActionMessageDataForDeployDetails,
    getRelevantAccountHashesFromArgs,
  };
};
